import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FieldControl,
  FormControl,
  IconControl,
  Loading,
} from 'spark-core-dx/components';
import { useCoreRoute } from 'spark-core-dx/hooks';
import { SystemTypes } from 'spark-core-dx/services';
import CardGridControl from '../resource/components/CardGridControl';
import WizardFooter from '../resource/components/wizard/WizardFooter';
import { sanitizeHtmlFragment } from '../../utilities/markupSanitizer';
import ClientCommandStatusTag from './ClientCommandStatusTag';
import {
  INTERNAL_NOTIFICATION_ITEM_FIELD_NAMES,
  INTERNAL_NOTIFICATION_RECIPIENT_METADATA,
  INTERNAL_NOTIFICATION_SEND_NOW_FIELD,
} from './clientCommunicationContracts';

const STEPS = [
  {
    key: 'content',
    title: 'Content and timing',
    description: 'Enter the notification content and choose when it should be delivered.',
  },
  {
    key: 'recipients',
    title: 'Users',
    description: 'Select one or more users in this client.',
  },
  {
    key: 'review',
    title: 'Review',
    description: 'Confirm the recipients, content, and timing before submission.',
  },
  {
    key: 'progress',
    title: 'Delivery status',
    description: 'See the latest delivery status and recipient results.',
  },
];

const formatDateTime = (value, fallback = 'Not set') => {
  if (!value) return fallback;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed.toLocaleString();
};

const notificationTypeLabel = (value) => (
  ({ 0: 'Alert', 1: 'Standard', 2: 'Important' }[Number(value)] || 'Standard')
);

const lifecycleActionLabel = (value) => ({
  Create: 'Send notification',
  UpdateScheduled: 'Update scheduled notification',
  SendNow: 'Send now',
  AddRecipients: 'Add users',
  ExtendExpiration: 'Extend expiration',
  StopRemaining: 'Stop remaining delivery',
  Cancel: 'Cancel notification',
  Retry: 'Retry failed notification',
  Resend: 'Resend as a new notification',
  EditAndResend: 'Edit and resend as a new revision',
  Duplicate: 'Duplicate as a new notification',
  DeleteFailed: 'Permanently remove failed notification',
}[value] || 'Manage notification');

const selectedFieldValue = (value) => {
  if (Array.isArray(value)) return selectedFieldValue(value[0]);
  if (value && typeof value === 'object') {
    return value.Value ?? value.value ?? value.Id ?? value.id ?? '';
  }
  return value ?? '';
};

const getEventManagerHref = (routePath, eventId) => {
  if (!eventId) return '';
  const screenRoute = String(routePath || '/event/manage')
    .trim()
    .replace(/\/+$/, '');
  return `${screenRoute}/${encodeURIComponent(eventId)}`;
};

const notificationProgressPresentation = (operation) => {
  const normalized = String(operation?.EventStatusLabel || '').trim().toLowerCase();
  if (operation?.StreamError) {
    return {
      title: 'Live delivery updates stopped',
      status: 'Updates unavailable',
      tone: 'error',
      alertTone: 'error',
      message: operation.StreamError,
    };
  }
  if (normalized.includes('partial')) {
    return {
      title: 'Notification partially sent',
      status: 'Partial failure',
      tone: 'error',
      alertTone: 'error',
      message: 'Some users did not receive the notification. Review the delivery updates below or open Event Manager for full details.',
    };
  }
  if (operation?.IsTerminal && operation?.IsSuccessful) {
    const scheduled = normalized.includes('scheduled');
    return {
      title: scheduled ? 'Notification scheduled' : 'Notification sent',
      status: scheduled ? 'Scheduled' : 'Completed',
      tone: 'success',
    };
  }
  if (operation?.IsTerminal && normalized.includes('cancel')) {
    return {
      title: 'Notification cancelled',
      status: 'Cancelled',
      tone: 'warning',
      alertTone: 'warning',
      message: 'The notification was cancelled before all remaining deliveries were completed.',
    };
  }
  if (operation?.IsTerminal || /(fail|error)/.test(normalized)) {
    return {
      title: 'Notification could not be sent',
      status: 'Failed',
      tone: 'error',
      alertTone: 'error',
      message: 'Notification delivery failed. Review the delivery updates below, then retry or open Event Manager for full details.',
    };
  }
  if (normalized.includes('progress')) {
    return {
      title: 'Sending notification',
      status: 'In progress',
      tone: 'neutral',
    };
  }
  return {
    title: 'Notification queued',
    status: 'Queued',
    tone: 'neutral',
  };
};

export const ClientCommunicationOperationProgress = ({
  operation,
  onTerminal,
  onSelectEvent,
  busy = false,
}) => {
  const { getScreen } = useCoreRoute();
  const terminalReported = useRef(false);
  const [selectionError, setSelectionError] = useState('');

  useEffect(() => {
    terminalReported.current = false;
  }, [operation?.EventId]);

  useEffect(() => {
    if (!operation?.IsTerminal || terminalReported.current) return;
    terminalReported.current = true;
    onTerminal?.(operation);
  }, [onTerminal, operation]);

  if (!operation) return <Loading />;
  const processed = Number(operation.ProcessedCount || 0);
  const total = Number(operation.TotalCount || 0);
  const presentation = notificationProgressPresentation(operation);
  const eventManagerHref = getEventManagerHref(
    getScreen('Event', '/event/manage/')?.RoutePath,
    operation.EventId,
  );
  const historyOptions = (operation.EventHistory || []).map((entry, index) => {
    const date = formatDateTime(entry.CreatedDate, 'Date unavailable');
    const action = lifecycleActionLabel(entry.Action);
    const current = entry.IsCurrent ? ' · Current' : '';
    return {
      Text: `${date} · ${entry.EventStatusLabel || 'Unknown'} · ${action}${current}`,
      DisplayName: `${date} · ${entry.EventStatusLabel || 'Unknown'} · ${action}${current}`,
      Value: entry.EventId,
      Sequence: index,
    };
  });
  return (
    <section className="command-notification-progress" aria-live="polite">
      {historyOptions.length ? (
        <div className="command-notification-progress__history-selector">
          <FieldControl
            fieldMetadata={{
              FieldName: 'DeliveryEventId',
              DisplayName: 'Delivery attempt',
              FieldType: SystemTypes.UIFieldType.SelectList,
              IsRequired: true,
              Description: 'Choose a delivery attempt to inspect its status, output, and result.',
            }}
            selectData={historyOptions}
            value={operation.EventId || ''}
            disabled={busy}
            groupId="notification-delivery-event-history"
            onChange={async (value) => {
              const eventId = String(selectedFieldValue(value) || '');
              if (eventId && eventId !== String(operation.EventId || '')) {
                setSelectionError('');
                const result = await onSelectEvent?.(eventId);
                if (result?.Success === false) {
                  setSelectionError(
                    result.DisplayMessage || 'The selected delivery attempt could not be loaded.',
                  );
                }
              }
            }}
            disableError
          />
        </div>
      ) : null}
      {selectionError ? (
        <div className="resource-wizard__inline-error" role="alert">
          {selectionError}
        </div>
      ) : null}
      {operation.StreamWarning ? (
        <div
          className="resource-wizard__alert resource-wizard__alert--warning"
          role="status"
        >
          <strong>Live updates unavailable</strong>
          <span>{operation.StreamWarning}</span>
        </div>
      ) : null}
      {presentation.message ? (
        <div
          className={[
            'resource-wizard__alert',
            presentation.alertTone && presentation.alertTone !== 'error'
              ? `resource-wizard__alert--${presentation.alertTone}`
              : '',
          ].filter(Boolean).join(' ')}
          role={presentation.alertTone === 'error' ? 'alert' : 'status'}
        >
          <strong>{presentation.title}</strong>
          <span>{presentation.message}</span>
        </div>
      ) : null}
      <div className="command-notification-progress__summary">
        <div>
          <strong>{presentation.title}</strong>
          <span>
            {operation.IsTerminal
              ? 'Delivery processing has finished.'
              : 'We are preparing and sending your notification.'}
          </span>
        </div>
        <ClientCommandStatusTag
          status={presentation.status}
          tone={presentation.tone}
        />
      </div>
      <div className="command-notification-progress__counts">
        <strong>{processed}</strong>
        <span>of {total || '—'} users processed</span>
      </div>
      {(operation.Logs || []).length ? (
        <div className="command-notification-progress__updates">
          <strong>Delivery updates</strong>
          <ol className="command-notification-progress__logs">
            {(operation.Logs || []).map((entry) => (
              <li key={entry.Id || `${entry.CreatedDate}-${entry.Message}`}>
                <time>{formatDateTime(entry.CreatedDate, '')}</time>
                <span>{entry.Message}</span>
                {entry.Processed != null ? (
                  <small>{entry.Processed} / {entry.Total ?? total}</small>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      ) : (
        <p className="command-onboarding__phase-note">
          No delivery updates have been recorded yet.
        </p>
      )}
      {(operation.ResultMessages || []).length ? (
        <div className="command-notification-progress__results">
          <strong>Operation result</strong>
          <ul>
            {operation.ResultMessages.map((result, index) => (
              <li key={`${index}-${result}`}>{result}</li>
            ))}
          </ul>
        </div>
      ) : null}
      {eventManagerHref ? (
        <a
          className="command-notification-progress__event-link"
          href={eventManagerHref}
          target="_blank"
          rel="noopener noreferrer"
        >
          View in Event Manager
        </a>
      ) : null}
      {!operation.IsTerminal ? (
        <p>Keep this window open while delivery is being processed.</p>
      ) : null}
    </section>
  );
};

const ReviewFact = ({ label, value }) => (
  <div className="command-notification-review__fact">
    <span>{label}</span>
    <strong>{value || 'Not provided'}</strong>
  </div>
);

const StepIntro = ({ step, trailing = null }) => (
  <div className="command-onboarding__step-intro">
    <div>
      <strong>{step.title}</strong>
      <span>{step.description}</span>
    </div>
    {trailing ? (
      <div className="command-onboarding__step-actions">
        {trailing}
      </div>
    ) : null}
  </div>
);

const ClientInternalNotificationWizard = ({
  open,
  draft,
  setDraft,
  recipients,
  metadata,
  operation,
  busy,
  onSaveDraft,
  onSubmit,
  canSaveDraft = false,
  canDispatch = false,
  onSelectEvent,
  onTerminal,
  onClose,
}) => {
  const [step, setStep] = useState(0);
  const [actionError, setActionError] = useState('');
  const readOnlyDelivery = Boolean(operation);
  const activeStep = STEPS[step] || STEPS[0];
  const selectedRecipientIds = draft?.RecipientProfileIds || [];
  const selectedRecipients = useMemo(() => (
    (recipients || []).filter((recipient) => (
      selectedRecipientIds.includes(recipient.ProfileId)
    ))
  ), [recipients, selectedRecipientIds]);
  const reviewMessageHtml = useMemo(
    () => sanitizeHtmlFragment(draft?.Message),
    [draft?.Message],
  );

  useEffect(() => {
    if (open) {
      setStep(operation?.EventId ? 3 : 0);
      setActionError('');
    }
  }, [open, operation?.EventId]);

  const editingExisting = Boolean(
    draft?.SourceRequestId
    || (draft?.RequestId && draft?.Action !== 'Create'),
  );
  const addingRecipients = editingExisting && draft?.Action === 'AddRecipients';
  const editingSavedDraft = Boolean(
    draft?.RequestId && draft?.Action === 'Create' && !draft?.SourceRequestId,
  );
  const wizardTitle = editingSavedDraft
    ? 'Edit Notification Draft'
    : editingExisting
      ? 'Manage Internal Notification'
      : 'Add Notification';
  const wizardStatusLabel = operation?.EventStatusLabel
    || (editingExisting ? lifecycleActionLabel(draft?.Action) : 'Draft');
  const displayedStep = readOnlyDelivery && step === 0
    ? {
      ...activeStep,
      title: 'Notification',
      description: 'Review the notification content used for this delivery.',
    }
    : readOnlyDelivery && step === 1
      ? {
        ...activeStep,
        description: 'Review the users selected for this delivery.',
      }
      : readOnlyDelivery && step === 2
        ? {
          ...activeStep,
          title: 'Review',
          description: 'Review what was sent and who received it.',
        }
        : addingRecipients && step === 0
          ? {
            ...activeStep,
            title: 'Notification',
            description: 'Review the notification before selecting additional users.',
          }
          : activeStep;
  const formFields = useMemo(() => {
    const sourceFields = metadata?.Fields || metadata?.fields || [];
    const fieldsByName = new Map(sourceFields.map((field) => [
      String(field?.FieldName || field?.fieldName || '').toLowerCase(),
      field,
    ]));
    const fields = INTERNAL_NOTIFICATION_ITEM_FIELD_NAMES.map((fieldName, index) => {
      const field = fieldsByName.get(fieldName.toLowerCase());
      if (!field) return null;
      return {
        ...field,
        DisplaySequence: index < 3 ? index + 1 : index + 2,
      };
    }).filter(Boolean);
    if (fields.length !== INTERNAL_NOTIFICATION_ITEM_FIELD_NAMES.length) return [];
    fields.splice(3, 0, INTERNAL_NOTIFICATION_SEND_NOW_FIELD);
    return fields;
  }, [metadata]);

  const canContinue = step === 0
    ? Boolean(canSaveDraft && draft?.Title?.trim() && draft?.Message?.trim()
        && (draft?.SendNow || draft?.StartDate))
    : step === 1
      ? selectedRecipientIds.length > 0
      : step === 2
        ? Boolean(canDispatch && draft?.RequestId && selectedRecipientIds.length > 0)
        : true;

  const handleNext = async () => {
    setActionError('');
    try {
      if (readOnlyDelivery && step < 3) {
        setStep((current) => Math.min(3, current + 1));
        return;
      }
      if (step === 0) {
        if (editingExisting) {
          setStep(1);
          return;
        }
        const result = await onSaveDraft(draft);
        if (result?.Success === false) {
          setActionError(result.DisplayMessage || 'The notification draft was not saved.');
          return;
        }
        if (result?.Draft) setDraft(result.Draft);
        setStep(1);
        return;
      }
      if (step === 1) {
        setStep(2);
        return;
      }
      if (step === 2) {
        setStep(3);
        const result = await onSubmit(draft);
        if (result?.Success === false) {
          setActionError(result.DisplayMessage || 'The notification was not sent.');
          setStep(2);
        }
        return;
      }
      if (operation?.IsRetryable) {
        const result = await onSubmit({
          ...draft,
          Action: 'Retry',
          RequestId: operation.RequestId,
        });
        if (result?.Success === false) {
          setActionError(result.DisplayMessage || 'The notification retry was not accepted.');
        }
        return;
      }
      onClose();
    } catch (error) {
      setActionError(error?.message || 'The notification action could not be completed.');
      if (step === 2) setStep(2);
    }
  };

  const closeDisabled = busy;
  if (!open) return null;

  const primaryActionLabel = readOnlyDelivery && step < 3
    ? 'Next >>'
    : step < 2
      ? 'Next >>'
      : step === 2
        ? 'Send'
        : operation?.IsRetryable ? 'Retry' : 'Close';

  return (
    <section
      className="resource-wizard command-onboarding command-notification-wizard"
      aria-label={`${wizardTitle} wizard`}
    >
      <div className="resource-wizard__content vertical-scrollable">
        <div className="resource-wizard__identity-topline command-onboarding__status-header">
          <div className="command-onboarding__status-title" role="status">
            {wizardTitle}
            {' · '}
            {wizardStatusLabel}
            {' · '}
            {`Step ${step + 1} of ${STEPS.length}`}
          </div>
          <div className="command-onboarding__status-actions">
            <IconControl
              iconKey="close"
              title={`Close ${wizardTitle}`}
              aria-label={`Close ${wizardTitle}`}
              disabled={closeDisabled}
              onClick={() => { if (!closeDisabled) onClose(); }}
            />
          </div>
        </div>

        {busy ? (
          <div className="command-entry__loading" role="status">
            <Loading />
            <span>Working with the notification request...</span>
          </div>
        ) : null}

        {actionError ? (
          <div className="resource-wizard__inline-error" role="alert">
            {actionError}
          </div>
        ) : null}

        <section
          className="resource-wizard__step-panel"
          aria-label={`${displayedStep.title} step`}
        >
          <StepIntro
            step={displayedStep}
            trailing={step === 1 ? (
              <span className="command-notification-wizard__selection-count">
                {`${selectedRecipients.length} selected`}
              </span>
            ) : null}
          />

          {step === 1 ? (
            <CardGridControl
              className="command-notification-wizard__recipients"
              rows={recipients || []}
              metadata={INTERNAL_NOTIFICATION_RECIPIENT_METADATA}
              cardFields={INTERNAL_NOTIFICATION_RECIPIENT_METADATA.Fields}
              rowKey={(recipient) => recipient.ProfileId}
              selectedRowKeys={selectedRecipientIds}
              onSelectedRowKeysChange={(keys) => setDraft((current) => ({
                ...current,
                RecipientProfileIds: keys,
              }))}
              selectable
              multiSelect
              searchable
              disabled={busy || readOnlyDelivery}
              activeFilterEnabled={false}
              emptyContent="No users are available in this client."
              minCardWidth="13rem"
              maxColumns={2}
            />
          ) : null}

          {step === 0 ? (
            <>
              {editingExisting && !readOnlyDelivery ? (
                <div className="command-notification-wizard__system-action">
                  <ReviewFact
                    label="Next action"
                    value={lifecycleActionLabel(draft?.Action)}
                  />
                  <span>This is selected automatically from the notification status.</span>
                </div>
              ) : null}
              {addingRecipients ? (
                <section className="command-notification-review">
                  <div className="command-notification-review__grid">
                    <ReviewFact
                      label="Type"
                      value={notificationTypeLabel(draft?.NotificationType)}
                    />
                    <ReviewFact
                      label="Delivery"
                      value={draft?.SendNow ? 'Send now' : formatDateTime(draft?.StartDate)}
                    />
                    <ReviewFact
                      label="Expiration"
                      value={formatDateTime(draft?.EndDate, 'No expiration')}
                    />
                  </div>
                  <article>
                    <strong>{draft?.Title}</strong>
                    <div
                      className="command-notification-review__message"
                      dangerouslySetInnerHTML={{ __html: reviewMessageHtml }}
                    />
                  </article>
                  <p>
                    {readOnlyDelivery
                      ? 'This is the notification content used for this delivery.'
                      : 'The existing notification content will remain unchanged.'}
                  </p>
                </section>
              ) : (
                <FormControl
                  metadata={{
                    ...metadata,
                    DisplayName: 'Internal Notification',
                    Fields: formFields,
                  }}
                  formFields={formFields}
                  itemData={draft}
                  setItemData={setDraft}
                  disableFooter
                  disableMinWidth
                  tabLayout="top"
                  onChange={(value, fieldName) => setDraft((current) => ({
                    ...current,
                    [fieldName]: value,
                  }))}
                  onSave={() => {}}
                  viewOnly={readOnlyDelivery}
                />
              )}
            </>
          ) : null}

          {step === 2 ? (
            <section className="command-notification-review">
              <div className="command-notification-review__grid">
                <ReviewFact
                  label="Recipients"
                  value={`${selectedRecipients.length} selected ${selectedRecipients.length === 1 ? 'user' : 'users'}`}
                />
                <ReviewFact label="Type" value={notificationTypeLabel(draft?.NotificationType)} />
                <ReviewFact label="Delivery" value={draft?.SendNow ? 'Send now' : formatDateTime(draft?.StartDate)} />
                <ReviewFact label="Expiration" value={formatDateTime(draft?.EndDate, 'No expiration')} />
                {editingExisting && !readOnlyDelivery ? (
                  <ReviewFact
                    label="Action"
                    value={draft?.Action === 'AddRecipients'
                      ? 'Send notification'
                      : lifecycleActionLabel(draft?.Action)}
                  />
                ) : null}
              </div>
              <article>
                <strong>{draft?.Title}</strong>
                <div
                  className="command-notification-review__message"
                  dangerouslySetInnerHTML={{ __html: reviewMessageHtml }}
                />
              </article>
              <p>
                {readOnlyDelivery
                  ? 'This is the notification and recipient list used for this delivery.'
                  : 'After you select Send, this window will show delivery progress and results.'}
              </p>
            </section>
          ) : null}

          {step === 3 ? (
            <ClientCommunicationOperationProgress
              operation={operation}
              onTerminal={onTerminal}
              onSelectEvent={onSelectEvent}
              busy={busy}
            />
          ) : null}
        </section>
      </div>

      <WizardFooter
        canCancelSetupFlow={false}
        showPreviousStepButton={step > 0}
        showSaveButton={false}
        showCompleteAction={false}
        hidePrimaryAction={Boolean(closeDisabled)}
        isLastStep={false}
        isComplete={Boolean(operation?.IsSuccessful)}
        saving={busy}
        hasActiveSectionStep
        activeSectionStepReadOnly={readOnlyDelivery}
        nextDisabled={busy || closeDisabled || (!readOnlyDelivery && !canContinue)}
        primaryActionLabel={primaryActionLabel}
        onPrevious={() => setStep((current) => Math.max(0, current - 1))}
        onNext={handleNext}
      />
    </section>
  );
};

export default ClientInternalNotificationWizard;
